/**
 * The local player's run: the deterministic PlayerSim driven in real time.
 *
 * Time: real elapsed time × sim.timeScale is game time, stepped in fixed ticks of 1/120 s of
 * game time. `simTime` is the engine-clock moment the last stepped tick ended; a frame steps
 * every tick that ends by the frame's time, and rendering interpolates between the last two
 * ticks. A frame never catches up more than MAX_CATCH_UP_MS: after a hidden tab or a locked
 * phone the run simply continues — the player lost that time, nothing is fast-forwarded
 * (the race is ranked by wall-clock time).
 *
 * Input: each tick takes the jump input of its own real-time slice (JumpInput.heldForTick),
 * so a press counts at the tick during which it happened, independent of the frame rate.
 */
import {
  BOOSTS,
  DT,
  LEVEL_TRANSITION_TICKS,
  type LevelData,
  PlayerSim,
  RESPAWN_DELAY_TICKS,
  type SimEvent,
  SPEEDS,
  TICK_MS,
  TICK_RATE,
} from '@cube-rush/shared';
import type { JumpInput } from '../input/jumpInput';
import { PoseTracker } from './pose';

/** Longest stretch of time one frame may simulate. */
export const MAX_CATCH_UP_MS = 100;
/**
 * After finishing a level the character keeps running through the finish this long (visual;
 * short enough to stay on the ground the levels have past their finish line).
 */
const MAX_OVERRUN_S = 1.2;

/** A simulation event with when and where it happened. */
export interface RunEvent {
  event: SimEvent;
  /** Engine-clock time at the end of the tick that produced it. */
  time: number;
  /** Level and attempt during that tick (a respawn or level start carries the new ones). */
  level: number;
  attempt: number;
  /** The attempt's tick counter right after that tick (PlayerSim.state.tick). */
  tick: number;
  /** A spider's teleport: the height it left, and the one it came to. */
  teleport?: { from: number; to: number };
}

/** Interpolated look of the local player for one frame. */
export interface RunPose {
  x: number;
  y: number;
  rot: number;
}

export class LocalRun {
  readonly sim: PlayerSim;
  readonly pose = new PoseTracker();
  private started = false;
  private simTime = 0;
  private lost = 0;
  /** Game seconds since the current level was completed (the character runs on). */
  private overrun = 0;
  /**
   * The race tick (PlayerSim.raceTicks) at which the current attempt began, or the next one
   * will begin: after a death or a completed level it is known in advance.
   */
  private attemptStart = 0;
  /** The last tick teleported a spider: its height jumps, it does not glide (renderPose). */
  private teleported = false;

  constructor(levels: readonly LevelData[], startLevel: number) {
    const last = levels.length - 1;
    const level = Number.isInteger(startLevel) ? Math.min(Math.max(startLevel, 0), last) : 0;
    this.sim = new PlayerSim(levels, { startLevel: level });
  }

  get isStarted(): boolean {
    return this.started;
  }

  /** Engine-clock time at which the last stepped tick ended (= the next one starts). */
  get lastTickTime(): number {
    return this.simTime;
  }

  /** Real time skipped because frames came too late (hidden tab, stalls), ms. */
  get lostTime(): number {
    return this.lost;
  }

  /** Game seconds the character has run on past the finish of the completed level. */
  get overrunSeconds(): number {
    return this.overrun;
  }

  /**
   * Game seconds into the current attempt as of lastTickTime: 0 at the start of its first
   * tick (the level start, a respawn), negative during the pause before the next attempt
   * (after a death or a completed level). The music's clock: beat 0 on the attempt's start.
   */
  get attemptTime(): number {
    return (this.sim.raceTicks - this.attemptStart) / TICK_RATE;
  }

  /** Real duration of one tick at the current boost time scale, ms. */
  get tickDuration(): number {
    return TICK_MS / this.sim.timeScale;
  }

  /**
   * Starts the run as of engine-clock time `at` (the race start, possibly a little in the
   * past when the first frame after the start comes late). Pending input is dropped: what
   * was pressed during the countdown does not count.
   */
  start(at: number, input: JumpInput): void {
    if (this.started) return;
    this.started = true;
    this.simTime = at;
    input.discard();
  }

  /** Applies the catch-up limit for a frame at `now`. Call before advance(). */
  prepare(now: number): void {
    if (!this.started) return;
    const behind = now - this.simTime;
    if (behind > MAX_CATCH_UP_MS) {
      this.lost += behind - MAX_CATCH_UP_MS;
      this.simTime = now - MAX_CATCH_UP_MS;
    }
  }

  /**
   * Steps every tick that ended by `now`, each with the input of its own time slice.
   * Events are appended to `out`. Returns the number of ticks stepped.
   */
  advance(now: number, input: JumpInput, out: RunEvent[]): number {
    if (!this.started) return 0;
    this.prepare(now);
    let ticks = 0;
    for (;;) {
      const end = this.simTime + this.tickDuration;
      if (end > now) break;
      this.step(input.heldForTick(end), end, out);
      this.simTime = end;
      ticks++;
    }
    return ticks;
  }

  /**
   * Dev harness only: steps `count` ticks at once, ignoring the clock, with the input from
   * `held(attemptTick)`. Used to skip ahead to an interesting spot for screenshots.
   */
  fastForward(count: number, held: (attemptTick: number) => boolean, out: RunEvent[]): void {
    for (let i = 0; i < count && !this.sim.finished; i++) {
      this.step(held(this.sim.state.tick), this.simTime, out);
    }
  }

  /**
   * Engine-clock time at which tick `attemptTick` of the current attempt starts (for bots that
   * inject input at exact moments). Boosts that speed up or slow down time run out on known
   * ticks and are accounted for; a boost used later, or a death, changes the answer.
   */
  tickStartTime(attemptTick: number): number {
    const ticks = attemptTick - this.sim.state.tick;
    if (ticks <= 0) return this.simTime + ticks * this.tickDuration;
    // A boost with `remaining` ticks left sets the pace of the next `remaining` ticks: walk the
    // stretches between the moments such boosts run out.
    const paced = this.sim.activeBoosts.filter((b) => (BOOSTS[b.type].timeScale ?? 1) !== 1);
    let time = this.simTime;
    for (let done = 0; done < ticks; ) {
      let scale = 1;
      let until = ticks;
      for (const boost of paced) {
        if (boost.remaining <= done) continue;
        scale *= BOOSTS[boost.type].timeScale ?? 1;
        until = Math.min(until, boost.remaining);
      }
      time += ((until - done) * TICK_MS) / scale;
      done = until;
    }
    return time;
  }

  /** Interpolation factor between the last two ticks for a frame at `now`. */
  alpha(now: number): number {
    if (!this.started) return 1;
    return Math.min(1, Math.max(0, (now - this.simTime) / this.tickDuration));
  }

  /** The player's interpolated position and rotation for a frame at `now`. */
  renderPose(now: number, out: RunPose): RunPose {
    const sim = this.sim;
    const s = sim.state;
    const a = this.alpha(now);
    out.x = sim.prevX + (s.x - sim.prevX) * a;
    out.y = this.teleported ? s.y : sim.prevY + (s.y - sim.prevY) * a;
    out.rot = this.pose.at(a);
    if (this.overrun > 0) {
      // Keep running through the finish while the next level loads.
      const before = Math.max(0, this.overrun - DT);
      out.x = s.x + SPEEDS[s.speed] * (before + (this.overrun - before) * a);
    }
    return out;
  }

  private step(held: boolean, time: number, out: RunEvent[]): void {
    const sim = this.sim;
    const level = sim.levelIndex;
    const attempt = sim.attempt;
    const events = sim.tick(held);
    const tick = sim.state.tick;
    let restarted = false;
    this.teleported = false;
    for (const event of events) {
      if (event.type === 'jump' && event.kind === 'teleport') {
        this.teleported = true;
        out.push({
          event,
          time,
          level,
          attempt,
          tick,
          teleport: { from: sim.prevY, to: sim.state.y },
        });
        continue;
      }
      out.push({ event, time, level, attempt, tick });
      if (event.type === 'respawn' || event.type === 'levelStart') {
        restarted = true;
        this.attemptStart = sim.raceTicks;
      } else if (event.type === 'death') {
        this.attemptStart = sim.raceTicks + RESPAWN_DELAY_TICKS;
      } else if (event.type === 'levelComplete' && sim.phase === 'levelComplete') {
        this.attemptStart = sim.raceTicks + LEVEL_TRANSITION_TICKS;
      }
    }
    if (restarted) {
      this.pose.reset();
      this.overrun = 0;
    } else if (sim.phase === 'levelComplete' || sim.phase === 'finished') {
      if (this.overrun < MAX_OVERRUN_S) this.overrun += DT;
      this.pose.step(sim.state);
    } else {
      this.pose.step(sim.state);
    }
  }
}
